// pages/AdminBankInfoPage.js
import React, { useState, useMemo } from "react";
import {
  Container,
  Card,
  CardBody,
  CardTitle,
  Input,
  Button,
  Row,
  Col,
  Spinner,
  Alert,
} from "reactstrap";
import DebouncedAsyncSelect from "./DebouncedAsyncSelect";

import axiosInstance from "utils/axiosInstance";
import Breadcrumbs from "../../../components/Common/Breadcrumb";

const AdminBankInfoPage = () => {
  document.title = "用戶銀行資訊 | 管理後台";

  const [query, setQuery] = useState("");
  const [searching, setSearching] = useState(false);
  const [searchResults, setSearchResults] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [bankInfo, setBankInfo] = useState(null);
  const [loadingBankInfo, setLoadingBankInfo] = useState(false);
  const [showFullInfo, setShowFullInfo] = useState(false);
  const [error, setError] = useState("");

  const loadUserOptions = async (inputValue) => {
    if (inputValue.length < 3) return [];

    try {
      const { data } = await axiosInstance.get("/knovia/admin/users", {
        params: { query: inputValue },
      });

      return data.users.map((user) => ({
        value: user._id,
        label: `${user.email}（${user.username}）`,
      }));
    } catch (err) {
      console.error("Error loading users:", err);
      return [];
    }
  };

  const searchUsers = async () => {
    setSearching(true);
    setError("");
    try {
      const { data } = await axiosInstance.get("/knovia/admin/users", {
        params: { query },
      });
      setSearchResults(data.users || []);
    } catch (err) {
      console.error(err);
      setError("搜尋失敗，請稍後再試");
    } finally {
      setSearching(false);
    }
  };

  const loadBankInfo = async (userId) => {
    setSelectedUser(null);
    setBankInfo(null);
    setShowFullInfo(false);
    setLoadingBankInfo(true);
    setError(""); // clear error

    try {
      const { data } = await axiosInstance.get(`/knovia/admin/bankInfo/${userId}`);
      setSelectedUser(userId);
      setBankInfo(data);
    } catch (err) {
      if (err.message === "No bank info found") {
        // Bank info not found is NOT an error, just no data
        setSelectedUser(userId);
        setBankInfo(null);
      } else {
        console.error(err);
        setError("無法讀取銀行資訊");
      }
    } finally {
      setLoadingBankInfo(false);
    }
  };

  const handleRevealFullInfo = async () => {
    const confirmReveal = window.confirm("確定要查看完整資訊？此行為將被紀錄。");
    if (!confirmReveal) return;

    try {
      const { data } = await axiosInstance.get(`/knovia/admin/bankInfo/full/${selectedUser}`);
      setBankInfo(data);
      setShowFullInfo(true);
    } catch (err) {
      console.error(err);
      alert("無法讀取完整資訊");
    }
  };

  const obfuscate = (val) => {
    if (!val) return "";
    if (val.length <= 4) return "*".repeat(val.length);
    return val.slice(0, 2) + "*".repeat(val.length - 4) + val.slice(-2);
  };

  return (
    <div className="page-content">
      <Container fluid>
        <Breadcrumbs title="管理後台" breadcrumbItem="用戶銀行資訊" />

        <Card>
          <CardBody>
            <CardTitle className="mb-4">搜尋用戶</CardTitle>

            <DebouncedAsyncSelect
              loadOptions={loadUserOptions}
              onItemSelect={(option) => loadBankInfo(option.value)}
              placeholder="輸入 Email 或姓名（至少 3 個字元）"
              noOptionsMessage={() => "找不到符合的用戶"}
              //multi={false}
              //isClearable={true}
            />

            {error && <Alert color="danger">{error}</Alert>}

            {searchResults.length > 0 && (
              <>
                <CardTitle className="mb-2">搜尋結果</CardTitle>
                <ul style={{ paddingLeft: 0, listStyleType: "none" }}>
                  {searchResults.map((user) => (
                    <li key={user._id} style={{ marginBottom: "0.5rem" }}>
                      {user.email}（{user.username}）{" "}
                      <Button size="sm" color="info" onClick={() => loadBankInfo(user._id)}>
                        查看銀行資訊
                      </Button>
                    </li>
                  ))}
                </ul>
              </>
            )}

            {loadingBankInfo && <Spinner size="sm" />}

            <div style={{
              border: "1.5px solid #f1dfaa",
              borderRadius: "0.4rem",
              padding: "2.5rem 3rem 2rem 3rem"
            }}>
              {selectedUser && !loadingBankInfo && (
                <>
                  <CardTitle>銀行帳戶資訊</CardTitle>
                  {bankInfo ? (
                    <>
                      <p><b>銀行代碼：</b>{bankInfo.bankCode}</p>
                      <p><b>帳號：</b>{bankInfo.accountNumber}</p>
                      <p><b>戶名：</b>{bankInfo.accountName}</p>
                      <p><b>身分證/統編：</b>{bankInfo.idNumber}</p>

                      {!showFullInfo && (
                        <Button
                          color="danger"
                          size="sm"
                          style={{ marginTop: "1rem" }}
                          onClick={handleRevealFullInfo}
                        >
                          查看完整資訊
                        </Button>
                      )}
                    </>
                  ) : (
                    <p>此用戶尚未填寫銀行資訊</p>
                  )}
                </>
              )}
            </div>

          </CardBody>
        </Card>
      </Container>
    </div>
  );
};

export default AdminBankInfoPage;



